import React, { useState, useEffect } from 'react';
import { AlignLeft } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow, SubRow,
    InlineInput, AIButton, AIUndoButton, PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';

const DUMMY_DATA = {
    question: 'If you could change one thing about our product, what would it be and why? 💡',
    settings: { multipleAnswers: false, responses: 5, showNames: false, emojiReactions: true, description: true, descText: 'Be honest — your feedback directly shapes our roadmap.', pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, imageUrl: null },
};

const OpenText = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [settings, setSettings] = useState({
        multipleAnswers: false,
        responses: 5,
        showNames: false,
        emojiReactions: true,
        description: false,
        descText: '',
        pollResults: 'hidden',
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);
    const API_BASE = API_PATH;

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text: question }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestion(question);
                    setQuestion(data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImproving(false);
        }
    };

    const handleUndoQuestion = () => {
        if (previousQuestion !== null) {
            setQuestion(previousQuestion);
            setPreviousQuestion(null);
        }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousDescText(text);
                    set('descText', data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImprovingDesc(false);
        }
    };

    const handleUndoDesc = () => {
        if (previousDescText !== null) {
            set('descText', previousDescText);
            setPreviousDescText(null);
        }
    };

    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.question !== undefined) setQuestion(content.question);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const openQuestionEditor = () => setEditingQuestion(true);
    const closeQuestionEditor = () => setEditingQuestion(false);
    const openDescEditor = () => setEditingDesc(true);
    const closeDescEditor = () => setEditingDesc(false);
    const handleKeyToEdit = (e, openFn) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFn(); }
    };
    const handleEditKeyDown = (e, closeFn) => {
        if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); closeFn(); }
    };

    const right = (
        <>
            <SettingsGroup title="Poll Settings">
                <SettingsRow label="Multiple answers" description="Participants can submit more than one answer">
                    <Toggle checked={settings.multipleAnswers} onChange={(e) => set('multipleAnswers', e.target.checked)} />
                </SettingsRow>
                {settings.multipleAnswers && (
                    <SubRow>
                        <span>Max responses per participant:</span>
                        <InlineInput
                            type="number"
                            value={settings.responses}
                            onChange={(e) => set('responses', Math.min(20, Math.max(2, Number(e.target.value) || 2)))}
                            min={2}
                            max={20}
                        />
                    </SubRow>
                )}

                <SettingsRow label="Show respondent names" description="Show respondent names and avatars next to their response">
                    <Toggle checked={settings.showNames} onChange={(e) => set('showNames', e.target.checked)} />
                </SettingsRow>

                <SettingsRow label="Emoji reactions" description="Participants can react to others' responses with emojis">
                    <Toggle checked={settings.emojiReactions} onChange={(e) => set('emojiReactions', e.target.checked)} />
                </SettingsRow>

                <SettingsRow label="Poll description" description="Add description visible to participants">
                    <Toggle checked={settings.description} onChange={(e) => set('description', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup>
                <AddImageSection
                    imageUrl={settings.imageUrl}
                    onImageChange={(url) => set('imageUrl', url)}
                />
            </SettingsGroup>
        </>
    );

    const left = (
        <div className={`${styles.questionCard} ${settings.imageUrl ? styles.questionCardWithImage : ''}`}>
            {settings.imageUrl && (
                <QuestionImage imageUrl={settings.imageUrl} onImageChange={(url) => set('imageUrl', url)} />
            )}
            <div className={styles.questionContent}>
                <div className={styles.questionLabelRow}>
                    <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                    <span className={styles.aiActions}>
                        <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                        {previousQuestion !== null && (
                            <AIUndoButton onClick={handleUndoQuestion} label="Undo" />
                        )}
                    </span>
                </div>
                {editingQuestion ? (
                    <input
                        className={styles.questionInput}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="Share your thoughts about this session."
                        autoFocus
                        onBlur={closeQuestionEditor}
                        onKeyDown={(e) => handleEditKeyDown(e, closeQuestionEditor)}
                    />
                ) : (
                    <Tooltip title="Click to edit question" placement="top">
                        <div
                            className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={openQuestionEditor}
                            onKeyDown={(e) => handleKeyToEdit(e, openQuestionEditor)}
                        >
                            {question || 'Type your question here...'}
                        </div>
                    </Tooltip>
                )}

                {settings.description && (
                    <div className={styles.questionDescription}>
                        <div className={styles.questionLabelRow}>
                            <span className={styles.descLabel}>Description</span>
                            <span className={styles.aiActions}>
                                <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                {previousDescText !== null && (
                                    <AIUndoButton onClick={handleUndoDesc} label="Undo" />
                                )}
                            </span>
                        </div>
                        {editingDesc ? (
                            <input
                                className={styles.descInput}
                                value={settings.descText}
                                onChange={(e) => set('descText', e.target.value)}
                                placeholder="Type description here..."
                                autoFocus
                                onBlur={closeDescEditor}
                                onKeyDown={(e) => handleEditKeyDown(e, closeDescEditor)}
                            />
                        ) : (
                            <Tooltip title="Click to edit description" placement="top">
                                <div
                                    className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openDescEditor}
                                    onKeyDown={(e) => handleKeyToEdit(e, openDescEditor)}
                                >
                                    {settings.descText || 'Type description here...'}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'open-text',
                title: question || 'Open Text',
                content: { question, settings }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<AlignLeft size={20} />}
            color="var(--cat-green)"
            title="Open Text"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            validate={() => !question.trim() ? 'Question is required.' : null}
            onSave={() => {
                setPreviousQuestion(null);
                setPreviousDescText(null);
                saveContent({ question, settings });
            }}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default OpenText;
